/**
 * @file ClientSSOWizard.js
 * @description Component for managing the Client SSO Wizard with multiple tabs.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SSODetailsTab from './tabs/SSODetailsTab';
import ClientSpecificationsTab from './tabs/ClientSpecificationsTab';
import GlobalSummaryTab from './tabs/GlobalSummaryTab';
import ContactsTab from './tabs/ContactsTab';
import {
    fetchClientSSOById,
    createClientSSO,
    updateClientSSO,
    fetchSystemSSOById,
} from '../../services/ssoService';
import styles from './sso.module.scss';
import Toast from '../ui/toasts/Toast';
import { computeGlobalSummary } from '../../services/ssoSummaryService';
import { downloadCSV } from '../../utils/utils';
import { useContext } from 'react';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { useAuthContext } from '../../store/Contexts';
import { SecurityAttributes, TenantTiers } from '../../utils/data/enums';
import { ROUTES } from '../../constants/routes';
import { getText } from '../../i18n';
const TABS = {
    SSO_DETAILS: 'SSO Details',
    CLIENT_SPECIFICATIONS: 'Client Specifications',
    GLOBAL_SUMMARY: 'Global Summary',
    CONTACTS: 'Contacts',
};

const ClientSSOWizard = () => {
    const { showModal } = useContext(ModalContext);
    const { hasAttribute, companyConfig, clientTierMeetsRequirement } = useAuthContext();
    const navigate = useNavigate();
    const { ssoClientId } = useParams();
    const [isAddMode, setIsAddMode] = useState(!ssoClientId);
    const [activeTab, setActiveTab] = useState(TABS.SSO_DETAILS);
    const [clientSSOData, setClientSSOData] = useState(null);
    const [systemSSOData, setSystemSSOData] = useState(null);
    const [originalMergedData, setOriginalMergedData] = useState(null);
    const [mergedSSOData, setMergedSSOData] = useState({
        name: '',
        url: '',
        tenantSSOStatusId: '',
        urlIsLocked: false,
        isAdditionalUserVisible: false,
        nameId: '',
        tenantSSOAttributes: [],
    });
    const [isLoading, setIsLoading] = useState(false);
    const [showDownloadButton, setShowDownloadButton] = useState(false);
    const [isLoadingSSOSelection, setIsLoadingSSOSelection] = useState(false);

    const isEditing = !!ssoClientId;

    const isClientSSOReadOnly = hasAttribute(
        SecurityAttributes.ClientSSOReadOnly,
    ) && !hasAttribute(SecurityAttributes.ClientSSOFullAccess);

    const shouldShowSharedWithAdditionalUsers =
        companyConfig?.application?.allow_additional_user &&
        clientTierMeetsRequirement(TenantTiers.Silver);

    useEffect(() => {
        if (ssoClientId) {
            loadSSOData(ssoClientId);
        } else {
            setIsLoading(false);
        }
    }, [ssoClientId]);

    useEffect(() => {
        if (clientSSOData && systemSSOData) {
            mergeSSOData();
        }
    }, [clientSSOData, systemSSOData]);

    useEffect(() => {
        if (activeTab === TABS.GLOBAL_SUMMARY) {
            setShowDownloadButton(true);
        } else {
            setShowDownloadButton(false);
        }
    }, [activeTab]);
    const loadSSOData = async clientSSOId => {
        try {
            setIsLoading(true);
            const fetchedClientSSO = await fetchClientSSOById(clientSSOId);
            const fetchedSystemSSO = await fetchSystemSSOById(
                fetchedClientSSO.ssoId,
            );
            setClientSSOData(fetchedClientSSO);
            setSystemSSOData(fetchedSystemSSO);
        } catch (error) {
            console.error('Failed to fetch SSO data', error);
            Toast.error(getText('msg_fc6bf2c49a8b'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSSOSelection = async ssoId => {
        try {
            if (ssoId) {
                setIsLoadingSSOSelection(true);
                const globalSSO = await fetchSystemSSOById(ssoId);
                setSystemSSOData(globalSSO);
                setMergedSSOData(prevData => ({
                    ...prevData,
                    ssoId: ssoId,
                    url: globalSSO.url,
                    nameId: globalSSO.nameId,
                    name: globalSSO.name,
                    urlIsLocked: globalSSO.urlIsLocked,
                    isAdditionalUserVisible: false,
                    tenantSSOStatusId: isAddMode
                        ? null
                        : prevData.tenantSSOStatusId,
                    encryptionCertificateId: globalSSO.encryptionCertificateId,
                    signingCertificateId: globalSSO.signingCertificateId,
                    ssoInitiationTypeId: globalSSO.ssoInitiationTypeId,
                    ssoVendorEnvironmentId: globalSSO.ssoVendorEnvironmentId,
                    tenantSSOAttributes: globalSSO.ssoAttributes.map(attr => ({
                        ...attr,
                        ssoAttributeId: attr.id,
                        ssoAttributeValueId: attr.ssoAttributeValueId || null,
                        literal: attr.literal || '',
                        edited: false,
                        id: 0,
                    })),
                }));
            } else {
                setSystemSSOData(null);
                setMergedSSOData(prevData => ({
                    ...prevData,
                    ssoId: null,
                    url: '',
                    name: '',
                    urlIsLocked: false,
                    isAdditionalUserVisible: false,
                    tenantSSOStatusId: isAddMode
                        ? null
                        : prevData.tenantSSOStatusId,
                }));
            }
        } catch (error) {
            console.error('Failed to fetch SSO data', error);
            Toast.error(getText('msg_fc6bf2c49a8b'));
        } finally {
            setIsLoadingSSOSelection(false);
        }
    };

    const mergeSSOData = () => {
        const mergedAttributes = systemSSOData.ssoAttributes.map(sysAttr => {
            const clientAttr = clientSSOData.tenantSSOAttributes.find(
                cAttr => cAttr.ssoAttributeId === sysAttr.id,
            );
            return {
                ...sysAttr,
                ssoAttributeId: sysAttr.id,
                ssoAttributeValueId: sysAttr.isLocked
                    ? sysAttr.ssoAttributeValueId
                    : clientAttr
                        ? clientAttr.ssoAttributeValueId
                        : sysAttr.ssoAttributeValueId,
                literal: sysAttr.isLocked
                    ? sysAttr.literal
                    : clientAttr
                        ? clientAttr.literal
                        : sysAttr.literal,
                edited: !!clientAttr && !sysAttr.isLocked,
                id: clientAttr ? clientAttr.id : 0,
            };
        });

        const mergedData = {
            ...systemSSOData,
            ...clientSSOData,
            url: systemSSOData.urlIsLocked
                ? systemSSOData.url
                : clientSSOData.url
                    ? clientSSOData.url
                    : systemSSOData.url,
            tenantSSOAttributes: mergedAttributes,
            name: systemSSOData.name,
            isAdditionalUserVisible:
                clientSSOData.isAdditionalUserVisible ?? false,
        };

        setMergedSSOData(mergedData);
        setOriginalMergedData(JSON.parse(JSON.stringify(mergedData)));
    };

    const handleTabChange = tab => {
        setActiveTab(tab);
    };

    const handleSSODetailsChange = updatedData => {
        if (isClientSSOReadOnly) {
            const hasSharedAuUpdate =
                Object.keys(updatedData).length === 1 &&
                Object.prototype.hasOwnProperty.call(
                    updatedData,
                    'isAdditionalUserVisible',
                );

            if (!hasSharedAuUpdate) {
                return;
            }
        }

        setMergedSSOData(prevData => ({
            ...prevData,
            ...updatedData,
        }));
    };

    const handleClientSpecificationsChange = updatedData => {
        if (isClientSSOReadOnly) {
            return;
        }

        setMergedSSOData(prevData => ({
            ...prevData,
            ...updatedData,
        }));
    };

    const handleSave = async () => {
        try {
            setIsLoading(true);
            let isActive = true;

            // If the SSO is status is disabled (ID 2), set isActive to false
            if (mergedSSOData.tenantSSOStatusId === 2) isActive = false;

            const payload = {
                id: clientSSOData?.id || 0,
                ssoId: systemSSOData.id,
                isActive: isActive,
                tenantId: clientSSOData?.tenantId || 0,
                tenantSSOStatusId: mergedSSOData.tenantSSOStatusId,
                name: mergedSSOData.name,
                url: mergedSSOData.urlIsLocked ? null : mergedSSOData.url,
                isAdditionalUserVisible: Boolean(
                    mergedSSOData.isAdditionalUserVisible,
                ),
                tenantSSOAttributes: mergedSSOData.tenantSSOAttributes
                    .map(attr => {
                        const existingClientAttr =
                            clientSSOData?.tenantSSOAttributes.find(
                                clientAttr =>
                                    clientAttr.ssoAttributeId ===
                                    attr.ssoAttributeId,
                            );

                        // Determine if the attribute should be included in the payload
                        const shouldInclude =
                            existingClientAttr ||
                            attr.isEdited ||
                            attr.id !== 0;

                        if (shouldInclude) {
                            return {
                                id: attr.id || 0,
                                tenantSSOId: clientSSOData?.id || 0,
                                ssoAttributeId: attr.ssoAttributeId,
                                ssoAttributeValueId: attr.ssoAttributeValueId,
                                literal: attr.literal,
                            };
                        }

                        return null;
                    })
                    .filter(Boolean), // Remove null entries
            };

            if (isEditing) {
                await updateClientSSO(payload);
                Toast.success(getText('msg_aa8320dcb015'));
            } else {
                await createClientSSO(payload);
                Toast.success(getText('msg_84cf570b9842'));
            }
            // navigate(ROUTES.SSO_LIST);`
        } catch (error) {
            console.error('Failed to save SSO', error);
            const { response } = error;
            const errorMessage =
                response?.data?.detail ||
                'There was an issue saving the SSO data.';
            Toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (checkUnsavedChanges()) {
            showModal({
                type: ModalTypes.ACTION,
                title: getText('msg_b78130c92f32'),
                subtitle: getText('msg_2f4e8fc9ac09'),
                text: getText('msg_f3cefc556397'),
                onYes: () => navigate(ROUTES.SSO_LIST),
                onNo: () => { },
                noButtonText: getText('msg_77dfd2135f4d'),
                yesButtonText: getText('msg_36fff63ccbcd'),
            });
        } else {
            navigate(ROUTES.SSO_LIST);
        }
    };

    const handleDownload = async () => {
        try {
            setIsLoading(true);
            const summaryData = await computeGlobalSummary(
                mergedSSOData,
                false,
            );
            downloadCSV(
                summaryData,
                `client_summary_${mergedSSOData.name}.csv`,
            );
        } catch (error) {
            console.error('Error downloading client SSO summary:', error);
            Toast.error(getText('msg_edf26bbbe5fe'));
        } finally {
            setIsLoading(false);
        }
    };

    const checkUnsavedChanges = () => {
        if (!originalMergedData) return false;

        const currentData = JSON.parse(JSON.stringify(mergedSSOData));
        const originalData = JSON.parse(JSON.stringify(originalMergedData));

        for (const key in originalData) {
            if (
                typeof originalData[key] !== 'object' &&
                originalData[key] !== currentData[key]
            ) {
                return true;
            }
        }

        if (
            originalData.tenantSSOAttributes.length !==
            currentData.tenantSSOAttributes.length
        ) {
            return true;
        }

        for (let i = 0; i < originalData.tenantSSOAttributes.length; i++) {
            const originalAttr = originalData.tenantSSOAttributes[i];
            const currentAttr = currentData.tenantSSOAttributes[i];

            if (
                originalAttr.ssoAttributeValueId !==
                currentAttr.ssoAttributeValueId ||
                originalAttr.literal !== currentAttr.literal
            ) {
                return true;
            }
        }

        return false;
    };

    return (
        <div className={`page-content ${styles.container}`}>
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center position-relative pb-3 flex-column flex-xl-row gap-2">
                        <h2 className="page-title float-start ms-4 m-0 col-auto col-lg-2 text-nowrap align-self-xl-baseline">
                            {ssoClientId ? 'Edit Client SSO' : 'Add Client SSO'}
                        </h2>
                        <ul className={`${styles.tabList2} col-auto`}>
                            {Object.values(TABS).map((tab, index) => (
                                <React.Fragment key={tab}>
                                    <li
                                        className={`px-3 text-nowrap ${styles.tabItem2} ${activeTab === tab
                                            ? styles.active
                                            : ''
                                            }`}
                                        onClick={() => handleTabChange(tab)}
                                    >
                                        {tab}
                                    </li>
                                </React.Fragment>
                            ))}
                        </ul>
                        <div
                            className={`col-auto col-lg-2 d-flex justify-content-end justify-content-lg-evenly align-self-xl-baseline`}
                            style={{ direction: 'rtl' }}
                        >
                            {activeTab === TABS.GLOBAL_SUMMARY &&
                                showDownloadButton &&
                                !!systemSSOData ? (
                                <span
                                    type="button"
                                    onClick={handleDownload}
                                    className="ing-text-buttons mx-3 text-nowrap"
                                >
                                    <i className="bi bi-cloud-download me-2 mx-1"></i>
                                    {getText('msg_5cd6ed01f2f6')}
                                </span>
                            ) : (

                                <>
                                    {((shouldShowSharedWithAdditionalUsers && isClientSSOReadOnly) || !isClientSSOReadOnly) && (
                                        <span
                                            type="button"
                                            onClick={handleSave}
                                            className="ing-text-buttons me-3 d-flex"
                                        >
                                            <i className="bi bi-check me-2" />
                                            {getText('msg_efc007a393f6')}
                                        </span>)}
                                    <span
                                        type="button"
                                        onClick={handleClose}
                                        className="ing-text-buttons me-3 d-flex"
                                    >
                                        <i className="bi bi-x me-2" />
                                        {getText('msg_bbfa773e5a63')}
                                    </span>
                                </>

                            )}
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="w-100 mt-5 pb-5 text-center">
                            {getText('msg_b04ba49f8486')}
                        </div>
                    ) : (
                        <div
                            className={`w-100 mt-5 pb-5 ${styles.tabContainer}`}
                        >
                            {activeTab === TABS.SSO_DETAILS && (
                                <SSODetailsTab
                                    data={mergedSSOData}
                                    onChange={handleSSODetailsChange}
                                    isAddMode={isAddMode}
                                    onSSOSelection={handleSSOSelection}
                                    isLoadingSSOSelection={
                                        isLoadingSSOSelection
                                    }
                                    onSave={handleSave}
                                    isClientSSOReadOnly={isClientSSOReadOnly}
                                    shouldShowSharedWithAdditionalUsers={shouldShowSharedWithAdditionalUsers}
                                />
                            )}
                            {activeTab === TABS.CLIENT_SPECIFICATIONS && (
                                <ClientSpecificationsTab
                                    data={mergedSSOData}
                                    onChange={handleClientSpecificationsChange}
                                    isSSSelected={!!systemSSOData || !isAddMode}
                                    onSave={handleSave}
                                    isClientSSOReadOnly={isClientSSOReadOnly}
                                    shouldShowSharedWithAdditionalUsers={shouldShowSharedWithAdditionalUsers}
                                />
                            )}
                            {activeTab === TABS.GLOBAL_SUMMARY && (
                                <GlobalSummaryTab
                                    data={mergedSSOData}
                                    isSSSelected={!!systemSSOData || !isAddMode}
                                />
                            )}
                            {activeTab === TABS.CONTACTS && (
                                <ContactsTab data={mergedSSOData} />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClientSSOWizard;
